// src/modules/activity/components/ActivityView/index.js
import React, { useState } from 'react';
import { Card, Button, Image, Modal, Badge } from 'react-bootstrap';
import ImageModal from '../../../../shared/components/ImageModal';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { formatDuration, calculatePace, penaltyDaysLate } from '../../../../shared/utils/formatters';
import Alert from '../../../../shared/components/Alert';
import RegisterActivityForm from '../RegisterActivityForm';
import ActivityReactions from '../../../feed/components/ActivityReactions';
import RoutePreview from '../RoutePreview';
import { useAuth } from '../../../../shared/hooks/useAuth';
import StravaBadge from '../../../../shared/components/StravaBadge';
import PointsPopover from '../../../../shared/components/PointsPopover';
import PenaltyPopover from '../../../../shared/components/PenaltyPopover';
import './styles.css';

const ACTIVITY_ICON_MAP = {
    running: 'bi-person-walking',
    cycling: 'bi-bicycle',
    swimming: 'bi-water',
    hiking: 'bi-tree',
    walking: 'bi-person-walking',
    football: 'bi-trophy',
    badminton: 'bi-circle',
    other: 'bi-activity'
};

const ACTIVITY_COLOR_CLASS = {
    running: 'text-danger',
    cycling: 'text-primary',
    swimming: 'text-info',
    hiking: 'text-success',
    walking: 'text-secondary',
    football: 'text-warning',
    badminton: 'text-primary',
    other: 'text-muted'
};

const getActivityIcon = (type) => ACTIVITY_ICON_MAP[type?.toLowerCase()] || 'bi-activity';
const getActivityColorClass = (type) => ACTIVITY_COLOR_CLASS[type?.toLowerCase()] || 'text-muted';

const ActivityView = ({
    activity,
    activityData,
    attachments,
    onEdit,
    onDelete,
    handleChange,
    handleFileChange,
    handleRemoveAttachment,
    alert,
    showAlert,
    onAlertClose,
    isLoading,
    isOwner,
    hasHandleExemptionsPermission,
    onRequestExemption,
    onReviewExemption,
    routePolyline,
    isParsingFile = false,
    gpsFilledFields = new Set(),
    isAnalyzing = false,
    aiSuggestionsEnabled = true,
    onToggleAiSuggestions = null,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showExemptionRequestModal, setShowExemptionRequestModal] = useState(false);
    const [exemptionNote, setExemptionNote] = useState('');
    const [reviewNote, setReviewNote] = useState('');
    const [enlargedImage, setEnlargedImage] = useState(null);
    const { user, hasPermission } = useAuth();

    const canModify = user?.Username === activity.username || hasPermission('activities:writeAny');
    const activityDate = new Date(activity.date);
    const activityType = activity.activityType?.charAt(0).toUpperCase() + activity.activityType?.slice(1);
    const iconClass = getActivityIcon(activity.activityType);
    const colorClass = getActivityColorClass(activity.activityType);

    return (
        <>
            <Card className="activity-detail-card">
                <Card.Header className="activity-detail-header">
                    <div className="activity-detail-user">
                        <Link to={`/profile/${activity.username}`} className="activity-detail-avatar-link">
                            {activity.profilePicture ? (
                                <Image
                                    src={activity.profilePicture}
                                    roundedCircle
                                    className="activity-detail-avatar"
                                    alt={`${activity.firstName} ${activity.lastName}`}
                                />
                            ) : (
                                <div className="activity-detail-avatar-placeholder">
                                    <i className="bi bi-person"></i>
                                </div>
                            )}
                        </Link>
                        <div className="activity-detail-user-info">
                            <Link to={`/profile/${activity.username}`} className="activity-detail-username">
                                {activity.firstName} {activity.lastName}
                            </Link>
                            <span className="activity-detail-type">
                                <i className={`bi ${iconClass} ${colorClass} me-1`}></i>
                                {activityType}
                                {activity.source === 'strava' && (
                                    <span className="ms-2">
                                        <StravaBadge stravaActivityId={activity.stravaActivityId} />
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="activity-detail-header-right">
                        <PointsPopover activity={activity} popoverId="points-popover-detail" placement="bottom">
                            <div className="activity-detail-points" style={{ cursor: 'help' }}>
                                <span className="points-value">{activity.points.toFixed(1)}</span>
                                <span className="points-label">points</span>
                            </div>
                        </PointsPopover>
                        {activity.penaltyFactor !== undefined && activity.penaltyFactor < 1.0 && activity.exemptionStatus !== 'approved' && (
                            <PenaltyPopover 
                                activity={activity} 
                                popoverId="penalty-popover-detail" 
                                placement="bottom"
                            >
                                <div className="activity-detail-penalty-badge">
                                    <span className="penalty-badge-value">
                                        <i className="bi bi-exclamation-triangle"></i>
                                        {activity.penaltyFactor}×
                                    </span>
                                    <span className="penalty-badge-label">{penaltyDaysLate(activity.penaltyFactor)}</span>
                                </div>
                            </PenaltyPopover>
                        )}
                        {isOwner && activity.penaltyFactor !== undefined && activity.penaltyFactor < 1.0 && activity.exemptionStatus !== 'approved' && activity.exemptionStatus !== 'pending' && activity.exemptionStatus !== 'rejected' && (
                            <Button
                                variant="outline-warning"
                                size="sm"
                                className="request-exemption-btn"
                                onClick={() => setShowExemptionRequestModal(true)}
                            >
                                <i className="bi bi-shield-check me-1"></i>Request Exemption
                            </Button>
                        )}
                        {canModify && !isEditing && (
                            <div className="action-buttons">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <i className="bi bi-pencil me-1"></i>Edit
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <i className="bi bi-trash me-1"></i>Delete
                                </Button>
                            </div>
                        )}
                        {isEditing && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </Card.Header>

                <Card.Body>
                    {alert.show && showAlert && (
                        <Alert
                            variant={alert.variant}
                            message={alert.message}
                            show={alert.show}
                            onClose={onAlertClose}
                        />
                    )}

                    {isEditing ? (
                        <RegisterActivityForm
                            activityData={activityData}
                            attachments={attachments}
                            handleChange={handleChange}
                            handleSubmit={(e) => {
                                onEdit(e);
                                setIsEditing(false);
                            }}
                            handleFileChange={handleFileChange}
                            handleRemoveAttachment={handleRemoveAttachment}
                            isLoading={isLoading}
                            alert={alert}
                            showAlert={showAlert}
                            isEditing={true}
                            routePolyline={routePolyline}
                            isParsingFile={isParsingFile}
                            gpsFilledFields={gpsFilledFields}
                            isAnalyzing={isAnalyzing}
                            aiSuggestionsEnabled={aiSuggestionsEnabled}
                            onToggleAiSuggestions={onToggleAiSuggestions}
                        />
                    ) : (
                        <>
                            <div className="activity-detail-date">
                                <i className="bi bi-calendar3 me-1"></i>
                                <span>
                                    {activityDate.toLocaleDateString(undefined, {
                                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                    })}
                                </span>
                                <span className="ms-3">
                                    <i className="bi bi-clock me-1"></i>
                                    {activityDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>

                            <div className="activity-detail-stats">
                                <div className="activity-stat-item">
                                    <i className="bi bi-geo-alt"></i>
                                    <span className="stat-value">{activity.distance.toFixed(2)} <span className="stat-unit">km</span></span>
                                    <span className="stat-label">Distance</span>
                                </div>
                                <div className="activity-stat-item">
                                    <i className="bi bi-clock"></i>
                                    <span className="stat-value">{formatDuration(activity.duration)}</span>
                                    <span className="stat-label">Duration</span>
                                </div>
                                <div className="activity-stat-item">
                                    <i className="bi bi-speedometer2"></i>
                                    <span className="stat-value">{calculatePace(activity.duration, activity.distance, activity.activityType)}</span>
                                    <span className="stat-label">Pace</span>
                                </div>
                                {activity.elevation > 0 && (
                                    <div className="activity-stat-item">
                                        <i className="bi bi-arrow-up-right"></i>
                                        <span className="stat-value">{activity.elevation} <span className="stat-unit">m</span></span>
                                        <span className="stat-label">Elevation</span>
                                    </div>
                                )}
                                {activity.calories > 0 && (
                                    <div className="activity-stat-item">
                                        <i className="bi bi-fire"></i>
                                        <span className="stat-value">{activity.calories} <span className="stat-unit">kcal</span></span>
                                        <span className="stat-label">Calories</span>
                                    </div>
                                )}
                            </div>

                            {routePolyline && (
                                <div className="activity-detail-route mt-3">
                                    <h6 className="mb-2">Route</h6>
                                    <RoutePreview routePolyline={routePolyline} activityType={activity.activityType} />
                                </div>
                            )}

                            <ActivityReactions
                                activityId={activity.activityId}
                                initialReactions={activity.reactions || {}}
                                initialReactionUsers={activity.reactionUsers || {}}
                            />

                            {activity.description && (
                                <p className="activity-detail-description">{activity.description}</p>
                            )}

                            {/* Penalty / Exemption Section */}
                            {activity.exemptionStatus && (
                                <div className="activity-detail-penalty mt-3">
                                    {activity.exemptionStatus === 'pending' && (
                                        <>
                                            <Badge bg="warning" className="mb-2">
                                                <i className="bi bi-hourglass-split me-1"></i>Exemption pending review
                                            </Badge>
                                            {activity.exemptionUserNote && (
                                                <div className="exemption-note mt-2">
                                                    <small className="text-muted">Your note:</small>
                                                    <p className="mb-0">{activity.exemptionUserNote}</p>
                                                </div>
                                            )}
                                            {hasHandleExemptionsPermission && (
                                                <div className="exemption-review mt-2">
                                                    <small className="text-muted">Admin review:</small>
                                                    <textarea
                                                        className="form-control form-control-sm mt-1"
                                                        rows="2"
                                                        placeholder="Add a review note (optional)"
                                                        value={reviewNote}
                                                        onChange={(e) => setReviewNote(e.target.value)}
                                                    ></textarea>
                                                    <div className="mt-2">
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => {
                                                                onReviewExemption('approve', reviewNote);
                                                                setReviewNote('');
                                                            }}
                                                        >
                                                            <i className="bi bi-check-lg me-1"></i>Approve
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => {
                                                                onReviewExemption('reject', reviewNote);
                                                                setReviewNote('');
                                                            }}
                                                        >
                                                            <i className="bi bi-x-lg me-1"></i>Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activity.exemptionStatus === 'approved' && (
                                        <>
                                            <Badge bg="success" className="mb-2">
                                                <i className="bi bi-check-circle me-1"></i>Exemption approved
                                            </Badge>
                                            {activity.exemptionUserNote && (
                                                <div className="exemption-note mt-2">
                                                    <small className="text-muted">Your note:</small>
                                                    <p className="mb-0">{activity.exemptionUserNote}</p>
                                                </div>
                                            )}
                                            {activity.exemptionAdminNote && (
                                                <div className="exemption-note mt-2">
                                                    <small className="text-muted">Admin note:</small>
                                                    <p className="mb-0">{activity.exemptionAdminNote}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activity.exemptionStatus === 'rejected' && (
                                        <>
                                            <Badge bg="danger" className="mb-2">
                                                <i className="bi bi-x-circle me-1"></i>Exemption rejected
                                            </Badge>
                                            {activity.exemptionUserNote && (
                                                <div className="exemption-note mt-2">
                                                    <small className="text-muted">Your note:</small>
                                                    <p className="mb-0">{activity.exemptionUserNote}</p>
                                                </div>
                                            )}
                                            {activity.exemptionAdminNote && (
                                                <div className="exemption-note mt-2">
                                                    <small className="text-muted">Admin note:</small>
                                                    <p className="mb-0">{activity.exemptionAdminNote}</p>
                                                </div>
                                            )}
                                        </>
                                    )}

                                </div>
                            )}

                            {activity.attachments?.length > 0 && (
                                <div className="activity-detail-images">
                                    {activity.attachments.map((attachment, index) => (
                                        <div
                                            key={index}
                                            className="activity-detail-thumbnail"
                                            onClick={() => setEnlargedImage(attachment.fullUrl || attachment.url)}
                                        >
                                            <Image
                                                src={attachment.url}
                                                alt={`Activity attachment ${index + 1}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this activity? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={onDelete}>
                        Delete Activity
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showExemptionRequestModal} onHide={() => { setShowExemptionRequestModal(false); setExemptionNote(''); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Request Exemption</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You are requesting an exemption for this activity's late registration penalty.</p>
                    <div className="mb-3">
                        <label htmlFor="exemptionNote" className="form-label">
                            Note (optional)
                        </label>
                        <textarea
                            id="exemptionNote"
                            className="form-control"
                            rows="3"
                            placeholder="Explain why you are requesting this exemption..."
                            value={exemptionNote}
                            onChange={(e) => setExemptionNote(e.target.value)}
                        ></textarea>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowExemptionRequestModal(false); setExemptionNote(''); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={() => {
                            onRequestExemption(exemptionNote);
                            setShowExemptionRequestModal(false);
                            setExemptionNote('');
                        }}
                    >
                        Submit Request
                    </Button>
                </Modal.Footer>
            </Modal>

            <ImageModal
                show={!!enlargedImage}
                imageSrc={enlargedImage}
                onClose={() => setEnlargedImage(null)}
            />
        </>
    );
};

ActivityView.propTypes = {
    activity: PropTypes.shape({
        activityType: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        distance: PropTypes.number.isRequired,
        calories: PropTypes.number.isRequired,
        duration: PropTypes.number.isRequired,
        points: PropTypes.number.isRequired,
        elevation: PropTypes.number.isRequired,
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        profilePicture: PropTypes.string,
        description: PropTypes.string,
        source: PropTypes.string,
        stravaActivityId: PropTypes.string,
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired
        })),
        penaltyFactor: PropTypes.number,
        exemptionStatus: PropTypes.string,
        exemptionUserNote: PropTypes.string,
        exemptionAdminNote: PropTypes.string
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleFileChange: PropTypes.func.isRequired,
    handleRemoveAttachment: PropTypes.func.isRequired,
    alert: PropTypes.shape({
        show: PropTypes.bool.isRequired,
        variant: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired
    }).isRequired,
    showAlert: PropTypes.bool.isRequired,
    activityData: PropTypes.shape({
        type: PropTypes.string.isRequired,
        distance: PropTypes.string.isRequired,
        calories: PropTypes.number.isRequired,
        elevation: PropTypes.string.isRequired,
        duration: PropTypes.string.isRequired,
        date: PropTypes.instanceOf(Date).isRequired,
        attachments: PropTypes.array.isRequired
    }).isRequired,
    attachments: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
    isOwner: PropTypes.bool,
    hasHandleExemptionsPermission: PropTypes.bool,
    onRequestExemption: PropTypes.func,
    onReviewExemption: PropTypes.func,
    routePolyline: PropTypes.string,
    isParsingFile: PropTypes.bool,
    gpsFilledFields: PropTypes.instanceOf(Set),
    isAnalyzing: PropTypes.bool,
    aiSuggestionsEnabled: PropTypes.bool,
    onToggleAiSuggestions: PropTypes.func,
};

export default ActivityView;
